import { useEffect, useRef, useState, useCallback } from 'react';
import { notifications } from '@mantine/notifications';
import { useIntl } from 'react-intl';
import { WEBSOCKET_URL } from '@/commons/constants';
import { Notification as NotificationType } from '@/users/components/NotificationCenter/types';

interface WebSocketMessage {
  type: string;
  [key: string]: any;
}

interface UseWebSocketOptions {
  token: string;
  url?: string;
  autoConnect?: boolean;
  reconnectInterval?: number;
  onNotification?: (notification: NotificationType) => void;
}

interface UseWebSocketReturn {
  connected: boolean;
  authenticated: boolean;
  subscribe: (resource: string, resourceId: string) => void;
  unsubscribe: (resource: string, resourceId: string) => void;
  send: (message: WebSocketMessage) => void;
  disconnect: () => void;
  reconnect: () => void;
}

export const useWebSocket = ({
  token,
  url = WEBSOCKET_URL,
  autoConnect = true,
  reconnectInterval = 5000,
  onNotification,
}: UseWebSocketOptions): UseWebSocketReturn => {
  const intl = useIntl();
  const ws = useRef<WebSocket | null>(null);
  const reconnectTimeout = useRef<NodeJS.Timeout | null>(null);
  const wasConnected = useRef(false);
  const [connected, setConnected] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const subscriptions = useRef<Set<string>>(new Set());

  const connect = useCallback(() => {
    if (ws.current?.readyState === WebSocket.OPEN) {
      return;
    }

    try {
      ws.current = new WebSocket(url);

      ws.current.onopen = () => {
        setConnected(true);
        wasConnected.current = true;
        if (token && ws.current) {
          ws.current.send(JSON.stringify({ type: 'authenticate', token }));
        }
      };

      ws.current.onmessage = (event) => {
        try {
          handleMessage(JSON.parse(event.data));
        } catch {
          notifications.show({
            color: 'red',
            title: intl.formatMessage({ id: 'error' }),
            message: intl.formatMessage({ id: 'websocketMessageError' }),
          });
        }
      };

      ws.current.onerror = () => {
        notifications.show({
          color: 'red',
          title: intl.formatMessage({ id: 'websocketError' }),
          message: intl.formatMessage({ id: 'websocketConnectionError' }),
        });
      };

      ws.current.onclose = () => {
        const hadConnection = wasConnected.current;
        setConnected(false);
        setAuthenticated(false);
        wasConnected.current = false;

        if (hadConnection) {
          notifications.show({
            color: 'orange',
            title: intl.formatMessage({ id: 'websocketDisconnected' }),
            message: intl.formatMessage({ id: 'attemptingReconnection' }),
            autoClose: 3000,
          });
        }

        if (autoConnect) {
          reconnectTimeout.current = setTimeout(connect, reconnectInterval);
        }
      };
    } catch {
      notifications.show({
        color: 'red',
        title: intl.formatMessage({ id: 'error' }),
        message: intl.formatMessage({ id: 'websocketConnectionFailed' }),
      });
    }
  }, [url, token, autoConnect, reconnectInterval, intl]);

  const handleMessage = useCallback(
    (notification: NotificationType) => {
      switch (notification.type) {
        case 'connected':
          // Silent - no notification needed
          break;

        case 'authenticated':
          setAuthenticated(true);

          notifications.show({
            color: 'green',
            title: intl.formatMessage({ id: 'success' }),
            message: intl.formatMessage({ id: 'websocketAuthenticated' }),
            autoClose: 2000,
          });

          subscriptions.current.forEach((sub) => {
            const [resource, resourceId] = sub.split(':');

            if (ws.current?.readyState === WebSocket.OPEN) {
              ws.current.send(
                JSON.stringify({ type: 'subscribe', resource, resourceId })
              );
            }
          });
          break;

        case 'subscribed':
          // Silent - no notification needed for subscriptions
          break;

        case 'unsubscribed':
          // Silent - no notification needed
          break;

        case 'update':
          notifications.show({
            color: 'blue',
            title: intl.formatMessage({ id: notification.type }),
            message: intl.formatMessage(
              { id: `notification.${notification.i18nKey}` },
              notification.data || {}
            ),
            autoClose: 2000,
          });

          if (onNotification) {
            onNotification(notification);
          }
          break;
        case 'notification':
          if (onNotification) {
            onNotification(notification);
          }
          break;

        case 'pong':
          // Silent - no notification for pong
          break;

        default:
          // Silent - unknown message types don't need notifications
          break;
      }
    },
    [intl]
  );

  const disconnect = useCallback(() => {
    if (reconnectTimeout.current) {
      clearTimeout(reconnectTimeout.current);
    }
    ws.current?.close();
    ws.current = null;
    setConnected(false);
    setAuthenticated(false);
  }, []);

  const reconnect = useCallback(() => {
    disconnect();
    setTimeout(connect, 100);
  }, [disconnect, connect]);

  const subscribe = useCallback(
    (resource: string, resourceId: string) => {
      if (!authenticated || ws.current?.readyState !== WebSocket.OPEN) {
        notifications.show({
          color: 'orange',
          title: intl.formatMessage({ id: 'warning' }),
          message: intl.formatMessage({ id: 'websocketNotReady' }),
        });
        return;
      }
      const sub = `${resource}:${resourceId}`;
      subscriptions.current.add(sub);
      ws.current?.send(
        JSON.stringify({ type: 'subscribe', resource, resourceId })
      );
    },
    [authenticated, intl]
  );

  const unsubscribe = useCallback(
    (resource: string, resourceId: string) => {
      if (authenticated && ws.current?.readyState === WebSocket.OPEN) {
        subscriptions.current.delete(`${resource}:${resourceId}`);
        ws.current.send(
          JSON.stringify({ type: 'unsubscribe', resource, resourceId })
        );
      }
    },
    [authenticated]
  );

  const send = useCallback(
    (message: WebSocketMessage) => {
      if (ws.current?.readyState === WebSocket.OPEN) {
        ws.current.send(JSON.stringify(message));
      } else {
        notifications.show({
          color: 'orange',
          title: intl.formatMessage({ id: 'warning' }),
          message: intl.formatMessage({ id: 'websocketNotConnected' }),
        });
      }
    },
    [intl]
  );

  useEffect(() => {
    if (autoConnect) {
      connect();
    }

    return () => {
      if (reconnectTimeout.current) {
        clearTimeout(reconnectTimeout.current);
      }
      ws.current?.close();
    };
  }, [connect, autoConnect]);

  return {
    connected,
    authenticated,
    subscribe,
    unsubscribe,
    send,
    disconnect,
    reconnect,
  };
};
